import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PawPrint, ChevronRight, X } from "lucide-react";

export default function AdminPetRecords() {
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await api.get(`/admin/pet-records${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    setRecords(r.data);
  };
  useEffect(() => { load(); }, [q]);

  return (
    <div data-testid="admin-pet-records">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-brand-soft">Pet Records</h1>
          <p className="text-sm text-brand-soft/60 mt-1">Lifelong medical records created by pet parents.</p>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by pet, owner, email, mobile…" className="bg-white border border-brand-maroon/10 rounded-full px-4 py-2 text-sm w-72" data-testid="pet-records-search" />
      </div>

      <div className="bg-white rounded-2xl border border-brand-maroon/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-brand-ivory text-brand-soft/70">
            <tr>
              <Th>Pet</Th>
              <Th>Owner</Th>
              <Th>Contact</Th>
              <Th>Vaccinations</Th>
              <Th>Documents</Th>
              <Th>Updated</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-brand-soft/60 italic">No pet records yet.</td></tr>
            )}
            {records.map((r) => (
              <tr key={r.id} className="border-t border-brand-maroon/5 hover:bg-brand-ivory/40 cursor-pointer" onClick={() => setSelected(r)} data-testid={`pet-record-row-${r.id}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-brand-ivory flex items-center justify-center overflow-hidden">
                      {r.pet?.photo ? <img src={r.pet.photo.startsWith("http") ? r.pet.photo : `${process.env.REACT_APP_BACKEND_URL}${r.pet.photo}`} alt="" className="w-full h-full object-cover" /> : <PawPrint className="w-4 h-4 text-brand-maroon/40" />}
                    </div>
                    <div>
                      <div className="font-medium">{r.pet?.name || "—"}</div>
                      <div className="text-xs text-brand-soft/60">{[r.pet?.species, r.pet?.breed].filter(Boolean).join(" · ")}</div>
                    </div>
                  </div>
                </Td>
                <Td>
                  <div>{r.owner?.name || r.owner_user?.name || "—"}</div>
                  <div className="text-xs text-brand-soft/60">{r.owner?.city}</div>
                </Td>
                <Td>
                  <div className="text-xs">{r.owner?.email || r.owner_user?.email}</div>
                  <div className="text-xs text-brand-soft/60">{r.owner?.mobile}</div>
                </Td>
                <Td>{(r.vaccinations || []).length}</Td>
                <Td>{(r.documents || []).filter((d) => d.category !== "PetPhoto").length}</Td>
                <Td><div className="text-xs text-brand-soft/60">{new Date(r.updated_at).toLocaleDateString()}</div></Td>
                <Td><ChevronRight className="w-4 h-4 text-brand-soft/40" /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <RecordDrawer record={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function RecordDrawer({ record, onClose }) {
  const share = `${window.location.origin}/p/${record.share_id}`;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="pet-record-drawer">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-brand-soft">{record.pet?.name || "Pet record"}</h2>
          <button onClick={onClose} className="p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <Info label="Species" v={record.pet?.species} />
          <Info label="Breed" v={record.pet?.breed} />
          <Info label="Gender" v={record.pet?.gender} />
          <Info label="Weight" v={record.pet?.weight} />
          <Info label="DOB" v={record.pet?.dob} />
          <Info label="Microchip" v={record.pet?.microchip} />
          <Info label="Owner" v={record.owner?.name} />
          <Info label="City" v={record.owner?.city} />
          <Info label="Email" v={record.owner?.email} />
          <Info label="Mobile" v={record.owner?.mobile} />
        </div>
        <a href={share} target="_blank" rel="noreferrer" className="text-xs text-brand-maroon underline break-all">Public link: {share}</a>

        <div className="mt-6">
          <div className="font-serif text-lg mb-2">Vaccinations ({(record.vaccinations || []).length})</div>
          <ul className="space-y-1 text-sm">
            {(record.vaccinations || []).map((v) => <li key={v.id}>· {v.name} — given {v.date_given}{v.next_due ? `, next due ${v.next_due}` : ""}</li>)}
            {(record.vaccinations || []).length === 0 && <li className="text-brand-soft/50 italic">None</li>}
          </ul>
        </div>

        <div className="mt-6">
          <div className="font-serif text-lg mb-2">Emergency</div>
          <div className="text-sm text-brand-soft/80"><b>Allergies:</b> {record.emergency?.allergies || "—"}</div>
          <div className="text-sm text-brand-soft/80"><b>Medications:</b> {record.emergency?.medications || "—"}</div>
          <div className="text-sm text-brand-soft/80"><b>Contact:</b> {record.emergency?.contact || "—"}</div>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;
const Info = ({ label, v }) => (
  <div><div className="text-[10px] uppercase tracking-widest text-brand-soft/50">{label}</div><div>{v || "—"}</div></div>
);
